import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import '../welcome.css';
import { FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
// Import background images
import parisFinal from '../images/paris-final1.png';
import kyotoFinal from '../images/kyoto5.jpg';
import veniceImage from '../images/venice-final.png';
import spainImage from '../images/parkguell-final1.png';
import latinAmericaImage from '../images/southamerica-final.png';
import koreaImage from '../images/korea-final1.png';

const AccountSetup = () => {
    const [setupStep, setSetupStep] = useState(1);
    const [setupData, setSetupData] = useState({
        learningLanguage: "",
        spanishRegion: "",
        languageLevel: "",
        daysOfReview: "",
        repeatPercentage: "",
        dailyStoryRepeatNumber: "",
        username: "",
        chineseCharacterType: ""
    });
    const [usernameStatus, setUsernameStatus] = useState({
        checking: false,
        available: null,
        message: ""
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, logOut } = useFirebase();

    // Check if user is authenticated, if not redirect to welcome page
    useEffect(() => {
        if (!isAuthenticated && !localStorage.getItem('access_token')) {
            navigate('/welcome');
        }
    }, [isAuthenticated, navigate]);

    // Add a failsafe check to prevent users from being stuck in setup
    useEffect(() => {
        // Failsafe: If user was erroneously redirected to account setup
        const checkSetupStatus = async () => {
            try {
                if (isAuthenticated || localStorage.getItem('access_token')) {
                    // Check if the user actually has settings already
                    logger.debug('AccountSetup - Running failsafe check for existing settings');
                    const response = await apiClient.get('/api/settings');
                    
                    if (response.ok) {
                        const data = await response.json();
                        
                        // If user has non-empty settings and a username, they likely don't need setup
                        if (data.learningLanguage && data.languageLevel && localStorage.getItem('username')) {
                            logger.info('AccountSetup - Failsafe: User already has settings and username, clearing needsAccountSetup flag');
                            localStorage.removeItem('needsAccountSetup');
                            
                            // Give user a manual way to exit setup
                            const shouldExit = window.confirm('It looks like you may have already completed account setup. Would you like to go to the home page?');
                            if (shouldExit) {
                                navigate('/');
                            }
                        }
                    }
                }
            } catch (error) {
                logger.error('AccountSetup - Error in failsafe check:', error);
            }
        };
        
        checkSetupStatus();
    }, [isAuthenticated, navigate]);

    // Update background image when language or Spanish region changes
    useEffect(() => {
        // Create a style element for our custom CSS
        let styleElement = document.getElementById('background-styles');
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = 'background-styles';
            document.head.appendChild(styleElement);
        }

        logger.debug(`AccountSetup - Updating background for ${setupData.learningLanguage}`);

        if (setupData.learningLanguage === 'French') {
            // Apply styles through a CSS rule instead of inline styles
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${parisFinal}");
                    background-size: cover;
                    background-position: center bottom;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (setupData.learningLanguage === 'Japanese') {
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${kyotoFinal}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (setupData.learningLanguage === 'Italian') {
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${veniceImage}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (setupData.learningLanguage === 'Korean') {
            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${koreaImage}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else if (setupData.learningLanguage === 'Spanish') {
            // For Spanish, check the region
            const spanishRegion = setupData.spanishRegion || 'Latin America';
            const backgroundImage = spanishRegion === 'Spain' ? spainImage : latinAmericaImage;

            styleElement.innerHTML = `
                body::before {
                    content: '';
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url("${backgroundImage}");
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    z-index: -1;
                    opacity: 1;
                }
                body {
                    background-color: transparent !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        } else {
            // Remove the ::before element when not using a background image
            styleElement.innerHTML = `
                body::before {
                    display: none;
                }
                body {
                    background-color: #f2f2f2 !important;
                    min-height: 100vh;
                }
                html, body, #root {
                    height: 100%;
                }
            `;
        }
    }, [setupData.learningLanguage, setupData.spanishRegion]);

    const handleSetupChange = (key, value) => {
        logger.debug(`AccountSetup - Setting ${key} to ${value}`);
        const newData = {
            ...setupData,
            [key]: value,
        };
        setSetupData(newData);

        // If username is being updated, store it in localStorage immediately
        if (key === 'username') {
            if (value.trim()) {
                localStorage.setItem('username', value.trim());
            }
            
            // Don't check if username is empty
            if (!value.trim()) {
                setUsernameStatus({
                    checking: false,
                    available: null,
                    message: ""
                });
                return;
            }

            // Set checking status
            setUsernameStatus({
                checking: true,
                available: null,
                message: "Checking availability..."
            });

            // Debounce the API call
            setTimeout(async () => {
                try {
                    const response = await apiClient.get(`/api/auth/check-username?username=${encodeURIComponent(value.trim())}`);
                    const data = await response.json();

                    if (response.ok) {
                        setUsernameStatus({
                            checking: false,
                            available: data.available,
                            message: data.available ? "" : "Username is already taken"
                        });
                    } else {
                        setUsernameStatus({
                            checking: false,
                            available: null,
                            message: data.error || "Failed to check username"
                        });
                    }
                } catch (error) {
                    logger.error("Error checking username:", error);
                    setUsernameStatus({
                        checking: false,
                        available: null,
                        message: "Error checking username"
                    });
                }
            }, 500); // 500ms debounce
            
            return; // End early for username path
        } else if (key === 'learningLanguage') {
            if (value !== 'Spanish') {
                newData.spanishRegion = '';
            }
            if (value !== 'Chinese') {
                newData.chineseCharacterType = '';
            } else if (!newData.chineseCharacterType) {
                // Set default Chinese character type
                newData.chineseCharacterType = 'Simplified';
            }
        }
    };

    const nextStep = () => {
        // Update settings in the backend on each step
        updateSettings();
        setSetupStep(setupStep + 1);
    };

    const prevStep = () => {
        if (setupStep > 1) {
            setSetupStep(setupStep - 1);
        }
    };

    // Function to update settings in backend
    const updateSettings = async () => {
        try {
            logger.debug('AccountSetup - Updating settings:', setupData);

            // Determine which settings to update based on current step
            const settingsToUpdate = {};

            switch (setupStep) {
                case 1:
                    settingsToUpdate.learningLanguage = setupData.learningLanguage;
                    if (setupData.learningLanguage === 'Spanish') {
                        settingsToUpdate.spanishRegion = setupData.spanishRegion;
                    }
                    if (setupData.learningLanguage === 'Chinese') {
                        settingsToUpdate.chineseCharacterType = setupData.chineseCharacterType;
                    }
                    break;
                case 2:
                    settingsToUpdate.languageLevel = setupData.languageLevel;
                    break;
                case 3:
                    settingsToUpdate.daysOfReview = setupData.daysOfReview;
                    break;
                case 4:
                    settingsToUpdate.repeatPercentage = setupData.repeatPercentage;
                    break;
                case 5:
                    settingsToUpdate.dailyStoryRepeatNumber = setupData.dailyStoryRepeatNumber;
                    break;
                case 6:
                    // Handle username update separately
                    return;
                default:
                    return;
            }

            // Update settings in the backend (use relative path for prod compatibility)
            // apiClient.put() returns parsed JSON on success, or throws on HTTP error
            const response = await apiClient.put('/api/settings', settingsToUpdate);

            // apiClient.put() returns the parsed data (or a Response object in some edge cases)
            let data = response;
            if (response && typeof response.json === 'function') {
                // It's a raw Response object
                if (!response.ok) {
                    throw new Error(`Failed to update settings: ${response.status}`);
                }
                data = await response.json();
            }

            logger.debug('AccountSetup - Settings saved successfully:', data);

            // Store settings in localStorage for other components to access
            Object.keys(settingsToUpdate).forEach(key => {
                localStorage.setItem(key, data[key] || setupData[key]);
            });

        } catch (err) {
            logger.error('AccountSetup - Error saving settings:', err);
        }
    };

    // Function to finalize account setup
    const finishSetup = async () => {
        try {
            logger.debug('AccountSetup - Finalizing account setup');

            // Update username in the profile
            const profileResponse = await apiClient.put('/api/profile', {
                username: setupData.username
            });

            if (!profileResponse.ok) {
                throw new Error(`Failed to update profile: ${profileResponse.status}`);
            }

            // Update all settings one last time to ensure everything is saved
            // apiClient.put() returns parsed JSON on success, or throws on HTTP error
            const settingsResponse = await apiClient.put('/api/settings', {
                learningLanguage: setupData.learningLanguage,
                languageLevel: setupData.languageLevel,
                daysOfReview: setupData.daysOfReview,
                repeatPercentage: setupData.repeatPercentage,
                dailyStoryRepeatNumber: setupData.dailyStoryRepeatNumber,
                spanishRegion: setupData.learningLanguage === 'Spanish' ? setupData.spanishRegion : '',
                chineseCharacterType: setupData.learningLanguage === 'Chinese' ? setupData.chineseCharacterType : ''
            });

            // If the response is a raw Response object (edge case), check .ok
            if (settingsResponse && typeof settingsResponse.ok !== 'undefined' && !settingsResponse.ok) {
                throw new Error(`Failed to update settings: ${settingsResponse.status}`);
            }

            // Clear the account setup flag
            localStorage.removeItem('needsAccountSetup');
            logger.debug('AccountSetup - Cleared needsAccountSetup flag');

            // Redirect to home page
            navigate('/');

        } catch (err) {
            logger.error('AccountSetup - Error finalizing setup:', err);
        }
    };

    // Handle logout
    const handleLogout = async () => {
        try {
            await logOut();
            navigate('/welcome');
        } catch (error) {
            console.error('Logout error:', error);
            localStorage.clear();
            navigate('/welcome');
        }
    };

    const renderSetupContent = () => {
        switch (setupStep) {
            case 1:
                return (
                    <div className="signup-step">
                        <h2>Select the language you'd like to start with...</h2>
                        <select
                            className="signup-settings-select"
                            value={setupData.learningLanguage}
                            onChange={(e) => {
                                const language = e.target.value;
                                if (language !== "") {
                                    handleSetupChange('learningLanguage', language);
                                }
                            }}
                        >
                            <option value="" disabled>Select language</option>
                            <option value="Chinese">Chinese</option>
                            <option value="French">French</option>
                            <option value="Italian">Italian</option>
                            <option value="Japanese">Japanese</option>
                            <option value="Korean">Korean</option>
                            <option value="Spanish">Spanish</option>
                        </select>

                        {setupData.learningLanguage === 'Spanish' && (
                            <>
                                <h3>Choose the region...</h3>
                                <select
                                    className="signup-settings-select"
                                    value={setupData.spanishRegion}
                                    onChange={(e) => handleSetupChange('spanishRegion', e.target.value)}
                                >
                                    <option value="" disabled>Select region</option>
                                    <option value="Spain">Spain</option>
                                    <option value="Latin America">Latin America</option>
                                </select>
                            </>
                        )}

                        {setupData.learningLanguage === 'Chinese' && (
                            <>
                                <h3>Choose the character type...</h3>
                                <select
                                    className="signup-settings-select"
                                    value={setupData.chineseCharacterType}
                                    onChange={(e) => handleSetupChange('chineseCharacterType', e.target.value)}
                                >
                                    <option value="" disabled>Select character type</option>
                                    <option value="Simplified">Simplified Chinese</option>
                                    <option value="Traditional">Traditional Chinese</option>
                                </select>
                            </>
                        )}

                        <div className="button-group">
                            {/* No back button for step 1 */}
                            <button
                                className="continue-button full-rounded"
                                onClick={nextStep}
                                disabled={
                                    !setupData.learningLanguage ||
                                    (setupData.learningLanguage === 'Spanish' && !setupData.spanishRegion) ||
                                    (setupData.learningLanguage === 'Chinese' && !setupData.chineseCharacterType)
                                }
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="signup-step">
                        <h2>Select your current level...</h2>
                        <select
                            className="signup-settings-select"
                            value={setupData.languageLevel}
                            onChange={(e) => handleSetupChange('languageLevel', e.target.value)}
                        >
                            <option value="" disabled>Select level</option>
                            <option value="Beginner">Beginner</option>
                            <option value="Intermediate">Intermediate</option>
                            <option value="Advanced">Advanced</option>
                        </select>

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={nextStep}
                                disabled={!setupData.languageLevel}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="signup-step">
                        <h2>Select how many days you'd like to repeat your sentences...</h2>
                        <p className="signup-note">
                            <i>Everyday, you'd be repeating the previous day's audio files as well. To not overwhelm yourself, I suggest max 3-4 days to maintain balance between new content and review.</i>
                        </p>
                        <select
                            className="signup-settings-select"
                            value={setupData.daysOfReview}
                            onChange={(e) => handleSetupChange('daysOfReview', parseInt(e.target.value))}
                        >
                            <option value="" disabled>Select days</option>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num}</option>
                            ))}
                        </select>

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={nextStep}
                                disabled={!setupData.daysOfReview}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="signup-step">
                        <h2>Select a % of new sentences that will contain your saved words...</h2>
                        <p className="signup-note">
                            <i>The words that you encounter and save in Daily Stories and your Chat with Bojee will appear in new sentences to reinforce your learning. Select a % of sentences to feature your words.</i>
                        </p>
                        <select
                            className="signup-settings-select"
                            value={setupData.repeatPercentage}
                            onChange={(e) => handleSetupChange('repeatPercentage', parseInt(e.target.value))}
                        >
                            <option value="" disabled>Select percentage</option>
                            {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(num => (
                                <option key={num} value={num}>{num}%</option>
                            ))}
                        </select>

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={nextStep}
                                disabled={!setupData.repeatPercentage && setupData.repeatPercentage !== 0}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className="signup-step">
                        <h2>Select the # of words you save in your daily stories to reappear in future ones...</h2>
                        <p className="signup-note">
                            <i>In Daily Stories, you can click on any word to look up its definition and save it to your collection. All future stories will highlight the words you've saved to reinforce your learning.
                                Recommended: &lt;5</i>
                        </p>
                        <select
                            className="signup-settings-select"
                            value={setupData.dailyStoryRepeatNumber}
                            onChange={(e) => handleSetupChange('dailyStoryRepeatNumber', parseInt(e.target.value))}
                        >
                            <option value="" disabled>Select number</option>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num}</option>
                            ))}
                        </select>

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={nextStep}
                                disabled={!setupData.dailyStoryRepeatNumber && setupData.dailyStoryRepeatNumber !== 0}
                            >
                                Continue <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="signup-step">
                        <h2>Create a Username</h2>
                        <div className="username-input-container">
                            <input
                                type="text"
                                className={`username-input ${usernameStatus.available === false ? 'input-error' : ''} ${usernameStatus.available === true ? 'input-success' : ''}`}
                                value={setupData.username}
                                onChange={(e) => handleSetupChange('username', e.target.value)}
                                placeholder="Enter your username"
                            />
                            <div className="username-status-indicator">
                                {usernameStatus.checking && (
                                    <div className="spinner"></div>
                                )}
                                {usernameStatus.available === true && (
                                    <span className="status-icon success">✓</span>
                                )}
                            </div>
                        </div>
                        {usernameStatus.available === false && (
                            <div className="username-error-message">{usernameStatus.message}</div>
                        )}

                        <div className="button-group">
                            <button className="back-button" onClick={prevStep}>
                                <FaArrowLeft />
                            </button>
                            <button
                                className="continue-button"
                                onClick={finishSetup}
                                disabled={!setupData.username || usernameStatus.available === false || usernameStatus.checking}
                            >
                                Finish Setup <FaArrowRight />
                            </button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="fullscreen-overlay">
            <div className="fullscreen-content">
                {renderSetupContent()}
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <a 
                        href="#" 
                        onClick={(e) => { 
                            e.preventDefault(); 
                            handleLogout(); 
                        }}
                        style={{ 
                            color: '#777', 
                            fontSize: '14px',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                        }}
                    >
                        Log out
                    </a>
                </div>
            </div>
        </div>
    );
};

export default AccountSetup;
